import { useContext } from 'react'

import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import logger from '@cash-web/shared/util-logger'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { Listener } from '@cash-web/shared-data-access-redux-inspect'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import {
  BlockerFlowInteractViewBlockerResponseStatus,
  WebViewUnsupportedFeature,
} from '@squareup/customer-data-events-web'

import { FlowReducerAction, FlowState } from './flowReducer'
import { ExtendedBlockerAction, getBlockerAction } from './useBlockerAction'
import { useFlowLogging } from './useFlowLogging'

export type UseFlowReducerProps = {
  initialState?: Partial<FlowState>
  onDismissAction?: (blockerAction: BlockerAction) => void
  onCompleteFlow: () => Promise<void>
  onCancelFlow: (override?: () => void) => Promise<void>
  onBlockerView: () => void
  onFlowResponse?: (status: BlockerFlowInteractViewBlockerResponseStatus) => void
}

export const useFlowStartSideEffect = (): Listener<FlowState, FlowReducerAction> => {
  const { logStartFlow } = useFlowLogging()
  const { clearError } = useContext(ErrorContext)
  return ({ nextState, action }) => {
    if (action.type !== 'FLOW_START') return

    clearError()
    logStartFlow(nextState)
  }
}

export const useFlowCompleteSideEffect = ({
  onCompleteFlow,
}: Pick<UseFlowReducerProps, 'onCompleteFlow'>): Listener<FlowState, FlowReducerAction> => {
  const { logCompleteFlow } = useFlowLogging()
  const { clearError } = useContext(ErrorContext)
  return async ({ prevState, action }) => {
    if (action.type !== 'FLOW_COMPLETE') return

    if (prevState.trackingFlowName) {
      logCompleteFlow(prevState)
    }
    clearError()
    await onCompleteFlow?.()
  }
}

export const useFlowCancelSideEffect = ({
  onCancelFlow,
  onDismissAction,
}: Pick<UseFlowReducerProps, 'onCancelFlow' | 'onDismissAction'>): Listener<FlowState, FlowReducerAction> => {
  const { logCancelFlow } = useFlowLogging()
  const logCdfEvent = useLogCashWebCdfEvent()
  const { clearError } = useContext(ErrorContext)
  const journey = useJourneyContext()
  return async ({ prevState, action }) => {
    if (action.type !== 'FLOW_CANCEL') return

    if (!prevState.isInProgress) {
      return
    }
    if (action.force !== true) {
      const prevStateDismissAction = prevState.activeBlockerDescriptor?.dismiss_action as ExtendedBlockerAction
      if (prevStateDismissAction?.end_flow_action != null) {
        prevStateDismissAction.end_flow_action.onCancelFlow = action.onCancelFlowOverride
      }
      if (prevStateDismissAction != null) {
        const blockerAction = getBlockerAction(prevStateDismissAction, journey)
        if (!blockerAction.isSupported) {
          logCdfEvent(
            WebViewUnsupportedFeature({
              source: 'useFlowCancelSideEffect.onDismissAction',
              feature: 'BLOCKER_ACTION',
              action: blockerAction.actionName,
            })
          )
        }
        if (blockerAction.isSupported) {
          return onDismissAction?.(prevStateDismissAction)
        }
      }
    }
    clearError()
    logCancelFlow(prevState)
    onCancelFlow?.(action.onCancelFlowOverride)
  }
}

export const useBlockerViewSideEffect = ({
  onBlockerView,
}: Pick<UseFlowReducerProps, 'onBlockerView'>): Listener<FlowState, FlowReducerAction> => {
  const { logBlockerView } = useFlowLogging()
  return ({ nextState, action }) => {
    if (action.type !== 'BLOCKER_VIEW') return

    logBlockerView(nextState)
    onBlockerView?.()
  }
}

export const useBlockerSubmitSideEffect = (): Listener<FlowState, FlowReducerAction> => {
  const { logBlockerSubmit } = useFlowLogging()
  const { clearError } = useContext(ErrorContext)
  return ({ prevState, action }) => {
    if (action.type !== 'BLOCKER_SUBMIT') return

    clearError()
    logBlockerSubmit(prevState)
  }
}

export const useFlowResponseSideEffect = ({
  onFlowResponse,
}: Pick<UseFlowReducerProps, 'onFlowResponse'>): Listener<FlowState, FlowReducerAction> => {
  const { logFlowResponse } = useFlowLogging()
  return ({ prevState, nextState, action }) => {
    if (action.type !== 'FLOW_RESPONSE') return

    logFlowResponse({
      prev: prevState,
      next: nextState,
      status: action.status,
      ...action.error,
    })
    onFlowResponse?.(action.status)
  }
}

export const clientScenarioLogger: Listener<FlowState, FlowReducerAction> = ({ prevState, nextState, action }) => {
  if (prevState.clientScenario !== nextState.clientScenario) {
    logger.addBreadcrumb(
      `clientScenario changed in action ${action.type} from ${prevState.clientScenario} to ${nextState.clientScenario} for flow ${nextState.trackingFlowName}`
    )
  }
}
